import React from "react";

const getCount = (value) => {
  const number = Number(value);

  return Number.isFinite(number)
    ? Math.max(0, number)
    : 0;
};

const CashFlowSummaryCard = ({
  data,
  onClick,
  onAmendmentClick,
  onReturnedClick,
  onRejectedClick,
  userRole = "",
  loading = false
}) => {
  const canViewAmendments = [
    "cashmaker",
    "cashchecker",
    "accounts"
  ].includes(String(userRole).trim().toLowerCase());

  const getTileAction = (key) => {
    if (key === "checker" || key === "accounts") {
      return onClick;
    }

    if (canViewAmendments) {
      if (key === "amendment") return onAmendmentClick;
      if (key === "returned") return onReturnedClick;
      if (key === "rejected") return onRejectedClick;
    }

    return undefined;
  };
  const checkerCount = getCount(
    data?.totalCashPendingChecker
  );

  const accountsCount = getCount(
    data?.totalCashPendingAccounts
  );

  const returnedCount = getCount(
    data?.totalCashReturnedToMaker
  );

  const rejectedCheckerCount = getCount(
    data?.totalCashRejectedByChecker
  );

  const rejectedAccountsCount = getCount(
    data?.totalCashRejectedByAccounts
  );

  const rejectedCount = getCount(
    data?.totalCashRejectedAwaitingAcceptance ??
    rejectedCheckerCount + rejectedAccountsCount
  );

  const amendmentCount = getCount(
    data?.totalCashAmendmentPendingAccounts
  );

  const dateAccessCount = getCount(
    data?.totalCashDateAccessPendingAccounts
  );

  const workflowCount = getCount(
    data?.totalCashWorkflowPendingAccounts ??
    amendmentCount + dateAccessCount
  );

  const totalCount = getCount(
    data?.totalCashActionPending ??
    checkerCount +
    accountsCount +
    returnedCount +
    rejectedCount +
    workflowCount
  );

  const items = [
    {
      key: "total",
      label: "Total Outstanding",
      value: totalCount,
      icon: "fa fa-list-alt",
      description: "All pending workflow actions",
      colorClass: "cash-flow-total"
    },
    {
      key: "checker",
      label: "Entry Approval — CashChecker",
      value: checkerCount,
      icon: "fa fa-user",
      description: "Action pending with CashChecker",
      colorClass: "cash-flow-checker"
    },
    {
      key: "accounts",
      label: "Entry Approval — Accounts",
      value: accountsCount,
      icon: "fa fa-check-square-o",
      description: "Action pending with Accounts",
      colorClass: "cash-flow-accounts"
    },
    {
      key: "returned",
      label: "Returned — Correction Required",
      value: returnedCount,
      icon: "fa fa-reply",
      description: "CashMaker: correct and resubmit",
      colorClass: "cash-flow-returned"
    },
    {
      key: "rejected",
      label: "Rejected — Accept / Reprocess",
      value: rejectedCount,
      icon: "fa fa-times-circle",
      description:
        `CashMaker action • Rejected by CashChecker: ` +
        `${rejectedCheckerCount.toLocaleString("en-IN")} • ` +
        `Accounts: ${rejectedAccountsCount.toLocaleString("en-IN")}`,
      colorClass: "cash-flow-rejected"
    },
    {
      key: "amendment",
      label: "Amendments & Date Access — Accounts",
      value: workflowCount,
      icon: "fa fa-pencil-square-o",
      description:
        `Pending with Accounts • Amendments: ` +
        `${amendmentCount.toLocaleString("en-IN")} • ` +
        `Date Access: ${dateAccessCount.toLocaleString("en-IN")}`,
      colorClass: "cash-flow-amendment"
    }
  ];

  return (
    <div
      className="card border-0 mb-3 cash-flow-summary"
      aria-busy={loading}
    >
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 cash-flow-header">
        <div>
          <h4 className="mb-0 cash-flow-title">
            Petty Cash — Pending Actions
          </h4>

          <div className="mt-1 cash-flow-subtitle">
            Entry approvals, maker actions and amendment approvals
          </div>
        </div>

        <button
          type="button"
          className="btn btn-sm cash-flow-details-button"
          onClick={onClick}
        >
          View Details
        </button>
      </div>

      <div className="cash-flow-body">
        <div className="row g-2">
          {items.map((item) => (
            <div
              key={item.key}
              className="col-12 col-md-6 col-xl-4"
            >
              <div
                className={`cash-flow-tile ${item.colorClass} ${typeof getTileAction(item.key) === "function"
                  ? "cash-flow-tile-clickable"
                  : ""
                  }`}
                role={
                  typeof getTileAction(item.key) === "function"
                    ? "button"
                    : undefined
                }
                tabIndex={
                  typeof getTileAction(item.key) === "function"
                    ? 0
                    : undefined
                }
                onClick={getTileAction(item.key)}
                onKeyDown={
                  typeof getTileAction(item.key) === "function"
                    ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        getTileAction(item.key)?.();
                      }
                    }
                    : undefined
                }
              >
                <div className="d-flex align-items-center gap-2">
                  <span className="cash-flow-icon">
                    <i
                      className={item.icon}
                      aria-hidden="true"
                    />
                  </span>

                  <div className="cash-flow-content">
                    <div className="cash-flow-count">
                      {loading
                        ? "…"
                        : item.value.toLocaleString("en-IN")}
                    </div>

                    <div className="cash-flow-type-counts">
                      <span>
                        Cash In:{" "}
                        <strong>
                          {loading
                            ? "…"
                            : getCount(
                              data?.cashTypeCounts?.[item.key]?.in
                            ).toLocaleString("en-IN")}
                        </strong>
                      </span>

                      <span className="cash-flow-type-divider" aria-hidden="true">
                        |
                      </span>

                      <span>
                        Cash Out:{" "}
                        <strong>
                          {loading
                            ? "…"
                            : getCount(
                              data?.cashTypeCounts?.[item.key]?.out
                            ).toLocaleString("en-IN")}
                        </strong>
                      </span>
                    </div>

                    {["total", "amendment"].includes(item.key) && (
                      <div className="cash-flow-type-counts">
                        <span>
                          Date Access:{" "}
                          <strong>
                            {loading
                              ? "…"
                              : dateAccessCount.toLocaleString("en-IN")}
                          </strong>
                        </span>
                      </div>
                    )}

                    <div className="mt-1 cash-flow-label">
                      {item.label}
                    </div>
                  </div>
                </div>

                <div className="mt-2 cash-flow-description">
                  {loading ? "Loading counts…" : item.description}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CashFlowSummaryCard;